/**
 * 来場者向け緊急/重要なお知らせサービス
 * ホーム画面で強制表示する公開中一覧と、確認（❌押下）登録を提供します
 */

import { getSupabaseClient } from '../../../services/supabase/client';
import { AppError } from '../../../shared/utils/errorHandling';
import { logError } from '../../../shared/utils/logger';
import { validateRequiredString, validateUuid } from '../../../shared/utils/validation';
import { buildVisitorImageUrl } from '../../../services/storage/visitorImageUrl';

/** お知らせ画像バケット名 */
const NOTICE_IMAGE_BUCKET = 'notice-images';

/** 緊急お知らせ既読登録RPC名 */
const VISITOR_ACKNOWLEDGE_EMERGENCY_NOTICE_RPC_NAME = 'visitor_acknowledge_emergency_notice';

/** 入力不正エラーコード */
export const VISITOR_EMERGENCY_NOTICE_INVALID_INPUT_CODE = 'VISITOR_EMERGENCY_NOTICE_INVALID_INPUT';

/** セッション不正エラーコード */
export const VISITOR_EMERGENCY_NOTICE_SESSION_INVALID_CODE = 'VISITOR_EMERGENCY_NOTICE_SESSION_INVALID';

/** 取得/登録失敗エラーコード */
export const VISITOR_EMERGENCY_NOTICE_FAILED_CODE = 'VISITOR_EMERGENCY_NOTICE_FAILED';

/**
 * RPCエラーに特定キーワードが含まれるか判定する
 * @param {{ message?: string } | null} rpcError - RPCエラー
 * @param {string} keyword - 判定キーワード
 * @returns {boolean} 含まれる場合true
 */
const includesRpcErrorMessage = (rpcError, keyword) => {
  if (!rpcError?.message) {
    return false;
  }

  return rpcError.message.includes(keyword);
};

/**
 * RPCエラーをアプリエラーコードへ変換する
 * @param {{ message?: string } | null} rpcError - RPCエラー
 * @returns {string} アプリ向けエラーコード
 */
const mapEmergencyNoticeRpcErrorCode = (rpcError) => {
  if (
    includesRpcErrorMessage(rpcError, 'INVALID_SESSION_TOKEN')
    || includesRpcErrorMessage(rpcError, 'INVALID_NOTICE_ID')
  ) {
    return VISITOR_EMERGENCY_NOTICE_INVALID_INPUT_CODE;
  }

  if (
    includesRpcErrorMessage(rpcError, 'SESSION_NOT_FOUND')
    || includesRpcErrorMessage(rpcError, 'VISITOR_INACTIVE')
  ) {
    return VISITOR_EMERGENCY_NOTICE_SESSION_INVALID_CODE;
  }

  return VISITOR_EMERGENCY_NOTICE_FAILED_CODE;
};

/**
 * Storageパスから公開URLを生成する
 * @param {any} supabaseClient - Supabaseクライアント
 * @param {string} storagePath - ストレージパス
 * @returns {string} 公開URL（無ければ空文字）
 */
const toNoticeImageUrl = (supabaseClient, storagePath) => {
  return buildVisitorImageUrl(supabaseClient, NOTICE_IMAGE_BUCKET, storagePath);
};

/**
 * 緊急お知らせの取得行を検証してJSオブジェクト化する
 * @param {any} row - 行
 * @param {any} supabaseClient - Supabaseクライアント
 * @returns {{ noticeId: string, title: string, body: string, imageUrl: string, publishedAt: string | null }} お知らせ
 */
const validateEmergencyNoticeRow = (row, supabaseClient) => ({
  noticeId: String(row?.notice_id ?? row?.id ?? ''),
  title: String(row?.title ?? ''),
  body: String(row?.body ?? ''),
  imageUrl: toNoticeImageUrl(supabaseClient, row?.image_path),
  publishedAt: row?.published_at ?? null,
});

/**
 * 公開中の緊急お知らせ一覧を取得する（新しい順）
 * 確認記録の有無では除外せず、ホームへ戻るたびに再提示できる一覧を返す
 * @returns {Promise<Array<{ noticeId: string, title: string, body: string, imageUrl: string, publishedAt: string | null }>>} 公開中の緊急お知らせ一覧
 * @throws {AppError} 取得に失敗した場合
 */
export const visitorGetPublishedEmergencyNotices = async () => {
  /** Supabaseクライアント */
  const supabaseClient = getSupabaseClient();

  /** 公開中の緊急お知らせ取得結果 */
  const { data, error } = await supabaseClient
    .from('notices')
    .select('id, title, body, image_path, published_at, created_at')
    .eq('status', 'published')
    .eq('is_emergency', true)
    .in('target_role', ['all', 'visitor'])
    .order('published_at', { ascending: false, nullsFirst: false })
    .order('created_at', { ascending: false });

  if (error) {
    logError('公開中の緊急お知らせ取得に失敗しました。', {
      code: error.code,
      message: error.message,
      details: error.details,
    });

    throw new AppError('緊急お知らせの取得に失敗しました。', {
      code: VISITOR_EMERGENCY_NOTICE_FAILED_CODE,
      statusCode: 500,
      details: { message: error.message },
    });
  }

  if (!Array.isArray(data)) {
    return [];
  }

  return data.map((row) => validateEmergencyNoticeRow(row, supabaseClient));
};

/**
 * 緊急/重要なお知らせを既読にする（❌押下時）
 * @param {string} sessionToken - セッショントークン
 * @param {string} noticeId - お知らせID
 * @returns {Promise<void>}
 * @throws {AppError} 入力・通信エラー時
 */
export const visitorAcknowledgeEmergencyNotice = async (sessionToken, noticeId) => {
  const sessionTokenValidation = validateRequiredString(sessionToken, 'セッショントークン');

  if (!sessionTokenValidation.isValid) {
    throw new AppError(sessionTokenValidation.message, {
      code: VISITOR_EMERGENCY_NOTICE_INVALID_INPUT_CODE,
      statusCode: 400,
    });
  }

  const noticeIdValidation = validateUuid(noticeId);

  if (!noticeIdValidation.isValid) {
    throw new AppError(noticeIdValidation.message, {
      code: VISITOR_EMERGENCY_NOTICE_INVALID_INPUT_CODE,
      statusCode: 400,
    });
  }

  /** Supabaseクライアント */
  const supabaseClient = getSupabaseClient();

  /** RPC結果 */
  const { error } = await supabaseClient.rpc(VISITOR_ACKNOWLEDGE_EMERGENCY_NOTICE_RPC_NAME, {
    p_session_token: sessionToken.trim(),
    p_notice_id: noticeId,
  });

  if (error) {
    /** マップ済みエラーコード */
    const mappedErrorCode = mapEmergencyNoticeRpcErrorCode(error);

    logError('visitor_acknowledge_emergency_notice RPC 実行に失敗しました。', {
      code: error.code,
      message: error.message,
      details: error.details,
      mappedErrorCode,
    });

    throw new AppError('緊急お知らせの既読登録に失敗しました。', {
      code: mappedErrorCode,
      statusCode: 500,
      details: { message: error.message },
    });
  }
};
